import React, { useState, useEffect, useCallback } from 'react';
import {
  TimeRangePreset,
  TIME_RANGE_PRESETS,
  COMPARISON_PERIODS,
  ComparisonPeriod,
} from '../../utils/timezone';

// ─── Custom Time Range type ────────────────────────────────────────────────────
export interface CustomTimeRange {
  from: number; // epoch ms
  to: number;   // epoch ms
}

// ─── Duration formatter (mirrors BehaviorTimelineV2) ───────────────────────────
function formatDuration(ms: number): string {
  const totalSeconds = Math.round(ms / 1000);
  if (totalSeconds < 60) return `${totalSeconds}s`;
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  if (minutes < 60) return seconds > 0 ? `${minutes}m ${seconds}s` : `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  if (hours < 24) return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
  const days = Math.floor(hours / 24);
  const remainHours = hours % 24;
  return remainHours > 0 ? `${days}d ${remainHours}h` : `${days}d`;
}

// ─── datetime-local helpers ───────────────────────────────────────────────────
function msToDatetimeLocal(ms: number): string {
  const d = new Date(ms);
  const pad = (n: number) => n.toString().padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function datetimeLocalToMs(s: string): number {
  const d = new Date(s);
  return isNaN(d.getTime()) ? 0 : d.getTime();
}

// ─── TimeRangeSelector ─────────────────────────────────────────────────────────
// Shared time range selector with preset pills + optional custom date picker.
// When customTimeRange is non-null, the "Custom" button is highlighted and an
// inline datetime-local picker is shown. Timezone is displayed read-only.

interface TimeRangeSelectorProps {
  /** Currently selected time range preset */
  timeRange: TimeRangePreset;
  /** Called when user selects a different time range preset */
  onTimeRangeChange: (preset: TimeRangePreset) => void;
  /** Current timezone display (read-only) */
  timezone: string;
  /** Currently selected comparison period (only shown in trend mode) */
  comparisonPeriod?: ComparisonPeriod['key'];
  /** Called when user selects a different comparison period */
  onComparisonPeriodChange?: (period: ComparisonPeriod['key']) => void;
  /** Whether to show comparison period selector (trend mode) */
  showComparison?: boolean;
  /** Custom time range (from/to epoch ms). When non-null, custom mode is active. */
  customTimeRange?: CustomTimeRange | null;
  /** Called when the user applies or resets a custom time range. null = exit custom mode. */
  onCustomTimeRangeChange?: (range: CustomTimeRange | null) => void;
  /** Additional CSS class name */
  className?: string;
}

const TimeRangeSelector: React.FC<TimeRangeSelectorProps> = ({
  timeRange,
  onTimeRangeChange,
  timezone,
  comparisonPeriod = 'yesterday',
  onComparisonPeriodChange,
  showComparison = false,
  customTimeRange = null,
  onCustomTimeRangeChange,
  className = '',
}) => {
  const isCustomMode = customTimeRange != null;
  const canCustom = onCustomTimeRangeChange != null;

  // ─── Custom date picker local state ──────────────────────────────────────
  const [startStr, setStartStr] = useState('');
  const [endStr, setEndStr] = useState('');
  const [error, setError] = useState<string | null>(null);

  // Sync local inputs when customTimeRange changes externally
  useEffect(() => {
    if (customTimeRange) {
      setStartStr(msToDatetimeLocal(customTimeRange.from));
      setEndStr(msToDatetimeLocal(customTimeRange.to));
    } else {
      setStartStr('');
      setEndStr('');
    }
    setError(null);
  }, [customTimeRange]);

  const handlePresetClick = useCallback((preset: TimeRangePreset) => {
    // Exit custom mode if active
    if (isCustomMode && onCustomTimeRangeChange) {
      onCustomTimeRangeChange(null);
    }
    onTimeRangeChange(preset);
  }, [isCustomMode, onCustomTimeRangeChange, onTimeRangeChange]);

  const handleCustomToggle = useCallback(() => {
    if (isCustomMode) {
      // Already custom → exit to preset
      onCustomTimeRangeChange?.(null);
    } else {
      // Enter custom mode with default 24h window ending now
      const now = Date.now();
      onCustomTimeRangeChange?.({ from: now - 24 * 60 * 60 * 1000, to: now });
    }
  }, [isCustomMode, onCustomTimeRangeChange]);

  const handleApply = useCallback(() => {
    const startMs = datetimeLocalToMs(startStr);
    const endMs = datetimeLocalToMs(endStr);

    if (startMs === 0 || endMs === 0) {
      setError('Invalid date format');
      return;
    }
    if (startMs >= endMs) {
      setError('Start must be before end');
      return;
    }
    if (endMs - startMs < 60000) {
      setError('Minimum range is 1 minute');
      return;
    }

    // Pass the user's raw selection through unchanged. The data fetch returns
    // empty for ranges with no data, so no silent clamping is needed — silently
    // rewriting the start/end would prevent the user from searching a specific
    // historical range (see historyLogPlan/06-simulated-current-analysis.md).
    setError(null);
    onCustomTimeRangeChange?.({ from: startMs, to: endMs });
  }, [startStr, endStr, onCustomTimeRangeChange]);

  const handleReset = useCallback(() => {
    onCustomTimeRangeChange?.(null);
  }, [onCustomTimeRangeChange]);

  return (
    <div className={className}>
      <div className="flex flex-wrap items-center gap-3">
        {/* Time range preset buttons */}
        <div className="flex items-center gap-1 bg-gray-100 dark:bg-slate-700 rounded-lg p-1">
          {TIME_RANGE_PRESETS.map((tr) => (
            <button
              key={tr.key}
              onClick={() => handlePresetClick(tr.key)}
              className={`px-2.5 py-1 text-xs font-medium rounded-md transition-all ${
                !isCustomMode && timeRange === tr.key
                  ? 'bg-white dark:bg-slate-500 text-gray-900 dark:text-white shadow-sm'
                  : 'text-gray-500 dark:text-gray-300 hover:text-gray-700 dark:hover:text-white'
              }`}
            >
              {tr.label}
            </button>
          ))}

          {/* Custom toggle button */}
          {canCustom && (
            <button
              onClick={handleCustomToggle}
              className={`px-2.5 py-1 text-xs font-medium rounded-md transition-all ${
                isCustomMode
                  ? 'bg-blue-500 text-white shadow-sm'
                  : 'text-gray-500 dark:text-gray-300 hover:text-gray-700 dark:hover:text-white'
              }`}
            >
              Custom
            </button>
          )}
        </div>

        {/* Current timezone display */}
        <div className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-gray-600 dark:text-gray-300 bg-white dark:bg-slate-700 border border-gray-200 dark:border-gray-600 rounded-lg">
          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          {timezone}
        </div>

        {/* Comparison period selector (trend mode only) */}
        {showComparison && onComparisonPeriodChange && (
          <div className="flex items-center gap-1 bg-gray-100 dark:bg-slate-700 rounded-lg p-1">
            {COMPARISON_PERIODS.map((cp) => (
              <button
                key={cp.key}
                onClick={() => onComparisonPeriodChange(cp.key)}
                className={`px-2.5 py-1 text-xs font-medium rounded-md transition-all ${
                  comparisonPeriod === cp.key
                    ? 'bg-white dark:bg-slate-500 text-gray-900 dark:text-white shadow-sm'
                    : 'text-gray-500 dark:text-gray-300 hover:text-gray-700 dark:hover:text-white'
                }`}
              >
                {cp.label}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Custom date picker panel (only in custom mode) */}
      {isCustomMode && (
        <div className="mt-2 p-3 bg-gray-50 dark:bg-slate-800 rounded-xl border border-gray-200 dark:border-gray-700">
          <div className="flex flex-wrap items-end gap-3">
            {/* Start date/time */}
            <div className="flex-1 min-w-[200px]">
              <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
                Start Date &amp; Time
              </label>
              <input
                type="datetime-local"
                value={startStr}
                onChange={(e) => { setStartStr(e.target.value); setError(null); }}
                className="w-full px-3 py-1.5 text-sm rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-slate-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
              />
            </div>

            {/* End date/time */}
            <div className="flex-1 min-w-[200px]">
              <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
                End Date &amp; Time
              </label>
              <input
                type="datetime-local"
                value={endStr}
                onChange={(e) => { setEndStr(e.target.value); setError(null); }}
                className="w-full px-3 py-1.5 text-sm rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-slate-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
              />
            </div>

            {/* Action buttons */}
            <div className="flex items-center gap-2">
              <button
                onClick={handleApply}
                className="px-3 py-1.5 text-xs font-medium rounded-lg bg-blue-500 text-white hover:bg-blue-600 transition-colors"
              >
                Apply
              </button>
              <button
                onClick={handleReset}
                className="px-3 py-1.5 text-xs font-medium rounded-lg border border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-slate-600 transition-colors"
              >
                Reset
              </button>
            </div>
          </div>

          {/* Error message */}
          {error && (
            <p className="mt-2 text-xs text-red-500 dark:text-red-400">{error}</p>
          )}

          {/* Duration badge */}
          {customTimeRange && (
            <div className="mt-2 flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
              <span>Duration:</span>
              <span className="px-1.5 py-0.5 rounded bg-blue-100 dark:bg-blue-900 text-blue-600 dark:text-blue-300 font-medium">
                {formatDuration(customTimeRange.to - customTimeRange.from)}
              </span>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default TimeRangeSelector;